import { Button, Card, CardContent, PageHeader, StatusChip } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { OwnerDataTable as DataTable } from "../../components/owner-data-table.tsx";
import {
  useCreateSessionTemplate,
  useOwnerState,
  useStartTonight,
} from "../../features/backoffice/queries.ts";
import type { VenueSessionStatus } from "../../features/backoffice/types.ts";

export const Route = createFileRoute("/_app/sessions")({ component: SessionsScreen });

const STATUS_TONE: Record<VenueSessionStatus, "info" | "success" | "warning" | "neutral"> = {
  scheduled: "info",
  open: "success",
  last_call: "warning",
  closing: "warning",
  closed: "neutral",
};

function TonightPanel() {
  const { data } = useOwnerState();
  const startTonight = useStartTonight();
  const sessions = data?.sessions ?? [];
  // At most one non-closed VenueSession per branch (FR-OWN-121 guard).
  const active = sessions.find((session) => session.status !== "closed");

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium">Tonight</p>
          {active ? (
            <p className="flex items-center gap-2 text-sm">
              <StatusChip tone={STATUS_TONE[active.status]}>{active.status}</StatusChip>
              {active.label}
              {active.socialPaused ? <StatusChip tone="warning">social paused</StatusChip> : null}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              No session is running for this branch yet.
            </p>
          )}
        </div>
        <Button
          disabled={Boolean(active) || startTonight.isPending}
          onClick={() => startTonight.mutate({ label: "Tonight" })}
        >
          Start tonight
        </Button>
      </CardContent>
    </Card>
  );
}

function SessionsScreen() {
  const { data } = useOwnerState();
  const create = useCreateSessionTemplate();
  return (
    <div className="space-y-6">
      <PageHeader
        title="Sessions"
        subtitle="Start tonight's real venue session here; ongoing order/gift handling stays in Staff Ops."
        actions={
          <Button variant="secondary" onClick={() => create.mutate({ label: "Weeknight" })}>
            Create template
          </Button>
        }
      />
      <TonightPanel />
      <DataTable
        rows={data?.sessionTemplates ?? []}
        getRowKey={(row) => row.id}
        columns={[
          { key: "label", header: "Label", cell: (row) => row.label },
          { key: "branch", header: "Branch", cell: (row) => row.branchId },
          {
            key: "features",
            header: "Enabled",
            cell: (row) =>
              Object.entries(row.enabledFeatures)
                .filter(([, value]) => value)
                .map(([key]) => (
                  <StatusChip key={key} tone="info">
                    {key}
                  </StatusChip>
                )),
          },
          { key: "lastCall", header: "Last call", cell: (row) => row.lastCallRule },
          { key: "close", header: "Expected close", cell: (row) => row.expectedCloseTime },
        ]}
      />
    </div>
  );
}
